/**
 * Trial Status Calculation Helpers
 *
 * Utilities for calculating and formatting trial status information
 * for display in the UI (sidebar, header banner, billing page).
 */

export interface TrialStatus {
  isOnTrial: boolean;
  daysRemaining: number | null;
  checksRemaining: number | null;
  checksUsed: number | null;
  checkLimit: number | null;
  expiresAt: Date | null;
  isApproachingLimit: boolean; // < 3 days OR < 5 checks
  isCritical: boolean; // 0 days OR 0 checks
}

export interface OrganizationBilling {
  id: string;
  plan: string | null;
  billingPeriod: string | null;
  subscriptionStatus: string | null;
  trialEndsAt: Date | string | null;
  trialCheckLimit: number | null;
  trialUsageCount: number | null;
  stripeCustomerId: string | null;
}

/**
 * Calculates comprehensive trial status from organization billing data
 */
export function calculateTrialStatus(org: OrganizationBilling | null | undefined): TrialStatus {
  // Default state for non-trial users
  const defaultState: TrialStatus = {
    isOnTrial: false,
    daysRemaining: null,
    checksRemaining: null,
    checksUsed: null,
    checkLimit: null,
    expiresAt: null,
    isApproachingLimit: false,
    isCritical: false,
  };

  if (!org || org.subscriptionStatus !== 'trialing' || !org.trialEndsAt) {
    return defaultState;
  }

  // Calculate days remaining
  const now = new Date();
  const expiresAt = new Date(org.trialEndsAt);
  const daysRemaining = Math.max(
    0,
    Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  );

  // Calculate checks remaining
  const checkLimit = org.trialCheckLimit || 25;
  const checksUsed = org.trialUsageCount || 0;
  const checksRemaining = Math.max(0, checkLimit - checksUsed);

  // Determine warning states
  const isApproachingLimit = daysRemaining < 3 || checksRemaining < 5;
  const isCritical = daysRemaining === 0 || checksRemaining === 0;

  return {
    isOnTrial: true,
    daysRemaining,
    checksRemaining,
    checksUsed,
    checkLimit,
    expiresAt,
    isApproachingLimit,
    isCritical,
  };
}

/**
 * Formats days remaining for display
 * Examples: "7 days", "1 day", "0 days"
 */
export function formatDaysRemaining(days: number): string {
  if (days === 0) return 'Trial expired';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}

/**
 * Formats checks remaining for display
 * Examples: "15/25 checks", "0/25 checks"
 */
export function formatChecksRemaining(used: number, limit: number): string {
  return `${used}/${limit} checks used`;
}

/**
 * Returns appropriate color scheme based on trial status
 */
export function getTrialColorScheme(status: TrialStatus): {
  bg: string;
  border: string;
  text: string;
  badge: 'amber' | 'red' | 'blue';
} {
  if (status.isCritical) {
    return {
      bg: 'bg-red-50 dark:bg-red-900/10',
      border: 'border-red-200 dark:border-red-900/30',
      text: 'text-red-900 dark:text-red-100',
      badge: 'red',
    };
  }

  if (status.isApproachingLimit) {
    return {
      bg: 'bg-amber-50 dark:bg-amber-900/10',
      border: 'border-amber-200 dark:border-amber-900/30',
      text: 'text-amber-900 dark:text-amber-100',
      badge: 'amber',
    };
  }

  return {
    bg: 'bg-blue-50 dark:bg-blue-900/10',
    border: 'border-blue-200 dark:border-blue-900/30',
    text: 'text-blue-900 dark:text-blue-100',
    badge: 'blue',
  };
}
